// src/components/MobileSplash.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowRight,
  RefreshCw,
  Activity,
  WifiOff,
  TrendingUp,
  TrendingDown,
  Share2,
} from 'lucide-react';
import { useRealTimePrices } from '../hooks/useRealTimePrices';

const AUTO_SECONDS = 15; // 15s como solicitado
const LS_SEEN_KEY = 'cofy_splash_seen_v2';

export const MobileSplash = ({ onEnter }) => {
  const [countdown, setCountdown] = useState(AUTO_SECONDS);
  const [isVisible, setIsVisible] = useState(false);
  const [quoteSnap, setQuoteSnap] = useState(null); // snapshot rápido vindo do quote.php

  const { coffeeData, dollarData, loading, error } = useRealTimePrices();
  const offline =
    typeof navigator !== 'undefined' && navigator.onLine === false;

  // Mostrar apenas no primeiro acesso
  useEffect(() => {
    try {
      const seen = localStorage.getItem(LS_SEEN_KEY);
      if (seen === '1') {
        // já viu — entrar direto
        if (typeof onEnter === 'function') onEnter();
        else if (typeof window !== 'undefined') window.location.href = '/';
        return;
      }
    } catch {
      /* ignore storage errors */
    }

    // Fade-in
    const fadeTimer = setTimeout(() => setIsVisible(true), 100);

    // Countdown + auto-enter
    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownTimer);
          try {
            localStorage.setItem(LS_SEEN_KEY, '1');
          } catch {}
          if (typeof onEnter === 'function') onEnter();
          else if (typeof window !== 'undefined') window.location.href = '/';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(fadeTimer);
      clearInterval(countdownTimer);
    };
  }, [onEnter]);

  const handleEnterNow = () => {
    try {
      localStorage.setItem(LS_SEEN_KEY, '1');
    } catch {}
    setCountdown(0);
    if (typeof onEnter === 'function') onEnter();
    else if (typeof window !== 'undefined') window.location.href = '/';
  };

  const handleShare = async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    const shareUrl = window.location.href || 'https://cofy.app/';
    const title = 'Terminal do Café – COFY';
    const text =
      'Acompanhe em tempo real o preço do café na Bolsa de NY, dólar e COFY em cofy.app.';

    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copiado para a área de transferência.');
      }
    } catch {
      // se o usuário cancelar o share, ignoramos
    }
  };

  // helpers
  const fmt = (n, d = 2) =>
    Number.isFinite(n) ? n.toFixed(d) : '—';
  const sign = (n) => (Number(n) >= 0 ? '+' : '');
  const brl = (v) =>
    Number.isFinite(v)
      ? v.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        })
      : 'R$ —';

  const asNum = (x) => {
    if (typeof x === 'number') return Number.isFinite(x) ? x : null;
    if (typeof x === 'string') {
      const n = Number(
        x.replace(/[^\d,.\-]/g, '').replace(',', '.')
      );
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  // === Snapshot do quote.php (agora atualiza a cada 1s) ===
  useEffect(() => {
    let alive = true;

    const fetchSnap = async () => {
      try {
        const r = await fetch(
          'https://cofy.app/api/quote.php?op=cofy_price',
          { cache: 'no-store' }
        );
        const j = await r.json();
        if (!alive) return;
        setQuoteSnap({
          kcCents: asNum(j?.kc_cents_per_lb), // ¢/lb
          usdPerLb: asNum(j?.usd_per_lb), // USD/lb
          usdbrl: asNum(j?.usdt_brl), // USD/BRL
        });
      } catch {
        /* silêncio */
      }
    };

    // primeira leitura imediata
    fetchSnap();
    // atualiza de 1 em 1 segundo
    const id = setInterval(fetchSnap, 1000);

    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // dados do hook (preferência), com fallback do quote
  const kcCentsHook = asNum(coffeeData?.price); // ¢/lb
  const usdbrlHook = asNum(dollarData?.price); // USD/BRL

  const kcCents = kcCentsHook ?? quoteSnap?.kcCents ?? null;
  const usdbrl = usdbrlHook ?? quoteSnap?.usdbrl ?? null;

  // máxima / mínima do dia
  const maxPrice = asNum(
    coffeeData?.high ??
      coffeeData?.max ??
      coffeeData?.highPrice
  );
  const minPrice = asNum(
    coffeeData?.low ??
      coffeeData?.min ??
      coffeeData?.lowPrice
  );

  // dados do topo (café NY em ¢/lb)
  const {
    price,
    change,
    changePercent,
    hasRealData,
    isPositive,
  } = useMemo(() => {
    const p = asNum(coffeeData?.price ?? kcCents);
    const ch = asNum(coffeeData?.change);
    const pct = asNum(coffeeData?.changePercent);
    const real = (coffeeData?.source || '')
      .toLowerCase()
      .includes('real');
    return {
      price: Number.isFinite(p) ? p : null,
      change: Number.isFinite(ch) ? ch : null,
      changePercent: Number.isFinite(pct) ? pct : null,
      hasRealData: real,
      isPositive: Number.isFinite(ch) ? ch >= 0 : true,
    };
  }, [coffeeData, kcCents]);

  return (
    <div className="fixed inset-0 bg-splash-dark z-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-green-500/10" />
      </div>

      {/* Card */}
      <div
        className={`relative w-full max-w-sm mx-auto bg-splash-card backdrop-blur-xl rounded-3xl p-8 border border-white/10 transition-all duration-1000 ${
          isVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-4'
        }`}
        role="dialog"
        aria-label="Cotação do café"
      >
        {/* Header + status */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-splash-text">
              Bolsa de NY — Café
            </h1>
            <div className="flex items-center space-x-1">
              {offline ? (
                <>
                  <WifiOff className="w-4 h-4 text-red-400" />
                  <span className="text-xs text-red-400">
                    OFF
                  </span>
                </>
              ) : loading ? (
                <>
                  <RefreshCw className="w-4 h-4 text-yellow-400 animate-spin" />
                  <span className="text-xs text-yellow-400">
                    LOAD
                  </span>
                </>
              ) : hasRealData ? (
                <>
                  <Activity className="w-4 h-4 text-green-400" />
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-xs text-green-400">
                    LIVE
                  </span>
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4 text-blue-400" />
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                  <span className="text-xs text-blue-400">
                    REAL
                  </span>
                </>
              )}
            </div>
          </div>
          <p className="text-lg text-splash-muted">
            {coffeeData?.symbol || 'KC=F'} • US¢/lb
          </p>
        </div>

        {/* Preço (¢/lb) */}
        <div
          className="text-center mb-6"
          aria-live="polite"
        >
          <div className="relative">
            <div
              className={`text-6xl font-black text-splash-text mb-3 ${
                loading ? 'animate-pulse' : 'animate-pulse-soft'
              }`}
            >
              {loading ? '---.--' : fmt(price, 2)}
            </div>

            {/* Variação */}
            <div
              className={`inline-flex items-center ${
                isPositive
                  ? 'bg-splash-positive/20 border-splash-positive/30'
                  : 'bg-red-500/20 border-red-500/30'
              } border rounded-full px-4 py-2 backdrop-blur-sm`}
            >
              {isPositive ? (
                <TrendingUp className="w-5 h-5 text-splash-positive mr-2" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400 mr-2" />
              )}

              <span
                className={`text-xl font-bold ${
                  isPositive
                    ? 'text-splash-positive glow-positive'
                    : 'text-red-400'
                }`}
              >
                {loading || change === null
                  ? '—.--'
                  : `${sign(change)}${fmt(
                      Math.abs(change),
                      2
                    )}`}
                {changePercent === null || loading
                  ? ''
                  : ` (${sign(changePercent)}${fmt(
                      Math.abs(changePercent),
                      2
                    )}%)`}
              </span>
            </div>
          </div>
        </div>

        {/* Máxima, mínima + café/dólar compactos */}
        <div className="text-xs text-splash-muted space-y-1 mb-6">
          <div className="flex justify-between">
            <span>Máxima do dia</span>
            <span className="font-semibold text-splash-text">
              {Number.isFinite(maxPrice)
                ? `${fmt(maxPrice, 2)} ¢/lb`
                : '—'}
            </span>
          </div>

          <div className="flex justify-between">
            <span>Mínima do dia</span>
            <span className="font-semibold text-splash-text">
              {Number.isFinite(minPrice)
                ? `${fmt(minPrice, 2)} ¢/lb`
                : '—'}
            </span>
          </div>

          <div className="mt-2 pt-2 border-t border-white/5 flex justify-between">
            <span>Café hoje</span>
            <span className="font-semibold text-splash-text">
              {Number.isFinite(price)
                ? `${fmt(price, 2)} ¢/lb`
                : '—'}
            </span>
          </div>

          <div className="flex justify-between">
            <span>Dólar (USDT/BRL)</span>
            <span className="font-semibold text-splash-text">
              {brl(usdbrl)}
            </span>
          </div>
        </div>

        {/* Origem dos dados */}
        <div className="text-center mb-6">
          <div className="mb-2 flex items-center justify-center space-x-2">
            <Activity className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-blue-400">
              {offline
                ? 'Sem conexão — exibindo quando voltar'
                : loading
                ? 'Carregando dados reais...'
                : 'Dados de mercado (KC=F + USD/BRL)'}
            </span>
          </div>
          {error && (
            <p className="mt-1 text-xs text-blue-400">
              {String(error)}
            </p>
          )}
        </div>

        {/* CTA texto */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-splash-text leading-tight">
            Acompanhe o mercado
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
              do café em tempo real
            </span>
          </h2>
        </div>

        {/* Botão entrar */}
        <button
          onClick={handleEnterNow}
          className="mb-3 flex w-full items-center justify-center space-x-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 font-bold text-white transition-all duration-300 hover:scale-105 hover:from-blue-500 hover:to-blue-600 hover:shadow-2xl hover:shadow-blue-500/25 group"
        >
          <span className="text-lg">Entrar no site</span>
          <ArrowRight className="h-6 w-6 transition-transform group-hover:translate-x-1" />
        </button>

        {/* Botão compartilhar */}
        <button
          onClick={handleShare}
          className="mb-6 flex w-full items-center justify-center space-x-2 rounded-2xl border border-blue-500/50 bg-blue-500/5 px-6 py-3 text-sm font-medium text-blue-100 transition-all duration-300 hover:bg-blue-500/10"
        >
          <Share2 className="h-4 w-4" />
          <span>Compartilhar cotações</span>
        </button>

        {/* Indicador de auto redirect */}
        <div className="text-center">
          <div className="text-sm text-splash-muted">
            Redirecionamento automático em
          </div>
          <div className="mt-1 text-lg font-bold text-splash-positive">
            {countdown}s
          </div>
        </div>
      </div>
    </div>
  );
};
